/**
 * Contract B1 (usage-QA fix wave 2026-10-07, PLAN.md): the questions the router
 * asks before it picks the browser or the server.
 *
 * Routing ruling (orchestrator 2026-10-07, recorded in IMAGE_EDITOR_V2.md): with
 * imageEditorBulk ON the browser goes first; the server is used only when the
 * browser cannot decode the file (EPS, RAW, unknown, a DecodeError) or the file
 * is over {@link BROWSER_BUDGET_PX}.
 */

import { canDecodeInBrowser } from './detect';
import { ImageFormat } from './types';

/**
 * Decoded RGBA pixels the browser path takes on, one image at a time.
 * Planner's choice (PLAN.md ruling 1, owner may overturn): flag-off already
 * exports 180 MP in under 4 s, so 100 MP would send working files to the slower
 * server path; 300 MP is 1.2 GB of RGBA, still inside a desktop tab.
 */
export const BROWSER_BUDGET_PX = 300e6;

/**
 * Why a decode failed. `unsupported`: the browser cannot read this format
 * (server may). `corrupt`: the bytes are damaged or use a coding the reader
 * lacks (server may still read it). `too-big`: over the budget (server).
 * `unreadable`: the browser could not read the file at all (moved, locked,
 * permissions); the server cannot help, the user must pick it again.
 */
export type DecodeErrorCode = 'unsupported' | 'corrupt' | 'too-big' | 'unreadable';

/** A decode failure whose `message` is safe to show as-is (no exception text). */
export class DecodeError extends Error {
    readonly code: DecodeErrorCode;
    constructor(code: DecodeErrorCode, message: string, cause?: unknown) {
        super(message);
        // Keep instanceof working when TS compiles to ES5 classes.
        Object.setPrototypeOf(this, DecodeError.prototype);
        this.name = 'DecodeError';
        this.code = code;
        if (cause !== undefined) { (this as any).cause = cause; }
    }
}

/** False for EPS, RAW and unknown: the only formats with no browser decoder. */
export function canTryInBrowser(f: ImageFormat): boolean {
    return f !== 'unknown' && canDecodeInBrowser(f);
}

/** True when width x height decoded pixels fit the browser budget. */
export function withinBrowserBudget(s: { width: number; height: number }): boolean {
    if (!s || !(s.width > 0) || !(s.height > 0)) { return false; }
    return s.width * s.height <= BROWSER_BUDGET_PX;
}
